import { DestroyRef, inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { ConfirmService } from '../confirm/confirm.service';

/** An editor that can say whether it holds edits that haven't been saved. */
export interface HasUnsavedChanges {
  hasUnsavedChanges(): boolean;
}

/**
 * Remembers what an editor's form looked like when it was last loaded or
 * saved, and compares against it. `read` returns the editable state, minus
 * anything that saves on its own (photo uploads, say).
 */
export class UnsavedChanges<T> {
  private snapshot: string | undefined;

  constructor(private readonly read: () => T) {}

  /** Call after the form is loaded and after every successful save. */
  mark(): void {
    this.snapshot = JSON.stringify(this.read());
  }

  /** True once the form differs from the last mark. Never true before the first mark. */
  get dirty(): boolean {
    return this.snapshot !== undefined && JSON.stringify(this.read()) !== this.snapshot;
  }
}

/**
 * Creates the tracker for an editor and, while the editor lives, asks the
 * browser to warn before a refresh or tab close with unsaved edits. Call it
 * from a field initializer (it needs the injection context).
 */
export function trackUnsavedChanges<T>(read: () => T): UnsavedChanges<T> {
  const changes = new UnsavedChanges(read);
  if (typeof window !== 'undefined') {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (changes.dirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    inject(DestroyRef).onDestroy(() => window.removeEventListener('beforeunload', onBeforeUnload));
  }
  return changes;
}

/** Route guard: leaving an editor with unsaved edits asks first. */
export const unsavedChangesGuard: CanDeactivateFn<HasUnsavedChanges> = (editor) =>
  !editor?.hasUnsavedChanges?.() ||
  inject(ConfirmService).confirm(
    'Discard your changes?',
    "Your edits on this page haven't been saved.",
    'Discard changes',
    { danger: true },
  );
